import { useCallback } from 'react';
import { useRequest } from './useRequest';
import { Integration, IntegrationCreate } from '../types/integration';
import { useCurrentUser } from '../contexts/UserContext';

export function useIntegration() {
  const listRequest = useRequest<Integration[]>();
  const createRequest = useRequest<Integration>();
  const updateRequest = useRequest<Integration>();
  const deleteRequest = useRequest<void>();
  const { currentUser, loading: userLoading } = useCurrentUser();

  // Helper function para obter empresaId com validação
  const getEmpresaId = useCallback((): string => {
    if (userLoading) {
      throw new Error('Carregando informações do usuário...');
    }
    const empresaId = currentUser?.id;
    if (!empresaId) {
      throw new Error('Empresa ID não encontrado. Usuário deve estar logado.');
    }
    return empresaId;
  }, [currentUser, userLoading]);

  const fetchIntegrations = useCallback(() => {
    if (userLoading || !currentUser) {
      return; // Não executar se usuário ainda não foi carregado
    }
    try {
      const empresaId = getEmpresaId();
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/empresas/${empresaId}/integracoes`;
      listRequest.execute(url);
    } catch (error) {
      // Erro será tratado pelo componente que chama
      console.warn('Não foi possível buscar integrações:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userLoading]);

  const createIntegration = useCallback(async (data: IntegrationCreate) => {
    if (userLoading || !currentUser) {
      throw new Error('Aguarde o carregamento das informações do usuário...');
    }
    const empresaId = getEmpresaId();
    await createRequest.execute(`${process.env.REACT_APP_API_BASE_URL}/api/empresas/${empresaId}/integracoes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userLoading]);

  const updateIntegration = useCallback(async (id: number, data: Partial<IntegrationCreate>) => {
    if (userLoading || !currentUser) {
      throw new Error('Aguarde o carregamento das informações do usuário...');
    }
    const empresaId = getEmpresaId();
    await updateRequest.execute(`${process.env.REACT_APP_API_BASE_URL}/api/empresas/${empresaId}/integracoes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    // Não chamar fetchIntegrations() automaticamente aqui
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userLoading]);

  const deleteIntegration = useCallback(async (id: number) => {
    if (userLoading || !currentUser) {
      throw new Error('Aguarde o carregamento das informações do usuário...');
    }
    const empresaId = getEmpresaId();
    await deleteRequest.execute(`${process.env.REACT_APP_API_BASE_URL}/api/empresas/${empresaId}/integracoes/${id}`, {
      method: 'DELETE',
    });
    // Não chamar fetchIntegrations() automaticamente aqui
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userLoading]);

  return {
    // Estados para listagem
    integrations: listRequest.data || [],
    listLoading: listRequest.loading,
    listError: listRequest.error,

    // Estados para operações de salvar (create/update)
    saveLoading: createRequest.loading || updateRequest.loading || deleteRequest.loading,
    saveError: createRequest.error || updateRequest.error || deleteRequest.error,


    // Estado do usuário atual
    userLoading,

    // Funções
    fetchIntegrations,
    createIntegration,
    updateIntegration,
    deleteIntegration,
  };
}